'use client';

import { useMemo, type ComponentType } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import {
  SignalsTableSelectAllHeader,
  SignalsTableSelectionCell,
} from './SignalsTableSelectionCells';
import {
  SignalAppWideCell,
  SignalCategoryCell,
  SignalCreatedAtCell,
  SignalDomainCell,
  SignalEndpointCell,
  SignalExecutionCell,
  SignalFindingCell,
  SignalNameCell,
  SignalSourceCell,
  SignalStageCell,
  SignalStatusCell,
} from './SignalsTableCells';
import { SignalActionsCell } from './SignalActionsCell';
import { isSignalDataRow, isSignalsSortableColumn } from './signals-list.model';
import type { SignalsTableRow } from './signals-grouping.model';

type SignalsDataColumn = {
  colId: string;
  header: string;
  width?: number;
  flex?: number;
  minWidth: number;
  pinned?: 'left';
  justify?: 'center';
  Cell: ComponentType<{ signal: GroupedSignal }>;
};

const SELECT_COLUMN_WIDTH = 40;
const ACTIONS_COLUMN_WIDTH = 72;
const preserveServerSortOrder = () => 0;

const renderSignalCell =
  ({ Cell, justify }: Pick<SignalsDataColumn, 'Cell' | 'justify'>) =>
  (params: ICellRendererParams<SignalsTableRow>) => {
    if (!isSignalDataRow(params.data)) {
      return null;
    }

    return (
      <Box
        h="full"
        display="flex"
        alignItems="center"
        justifyContent={justify}
        minW={0}
      >
        <Cell signal={params.data.signal} />
      </Box>
    );
  };

export function useSignalsTableColumnDefs({
  isRejectedView,
  showSourceColumn,
  showAppWideColumn,
  canSort,
}: {
  isRejectedView: boolean;
  showSourceColumn: boolean;
  showAppWideColumn: boolean;
  canSort: boolean;
}): ColDef<SignalsTableRow>[] {
  const t = useTranslations('proxy.signals.table');
  const tFilters = useTranslations('proxy.signals.filters');
  const tLifecycle = useTranslations('designSystem.signals.lifecycle');

  return useMemo(() => {
    const columns: Array<SignalsDataColumn | null> = [
      {
        colId: 'name',
        header: t('name'),
        width: 280,
        minWidth: 220,
        pinned: 'left',
        Cell: SignalNameCell,
      },
      {
        colId: 'domain',
        header: t('domain'),
        width: 160,
        minWidth: 120,
        Cell: SignalDomainCell,
      },
      {
        colId: 'relatedEndpoint',
        header: t('relatedEndpoint'),
        flex: 1,
        minWidth: 200,
        Cell: SignalEndpointCell,
      },
      showAppWideColumn
        ? {
            colId: 'appWide',
            header: tFilters('appWide.label'),
            width: 110,
            minWidth: 90,
            Cell: SignalAppWideCell,
          }
        : null,
      isRejectedView
        ? null
        : {
            colId: 'status',
            header: tLifecycle('status.label'),
            width: 200,
            minWidth: 160,
            Cell: SignalStatusCell,
          },
      isRejectedView
        ? null
        : {
            colId: 'stage',
            header: tLifecycle('stage.label'),
            width: 120,
            minWidth: 100,
            Cell: SignalStageCell,
          },
      showSourceColumn
        ? {
            colId: 'source',
            header: t('source'),
            width: 130,
            minWidth: 100,
            Cell: SignalSourceCell,
          }
        : null,
      {
        colId: 'category',
        header: tFilters('category'),
        width: 180,
        minWidth: 140,
        Cell: SignalCategoryCell,
      },
      isRejectedView
        ? null
        : {
            colId: 'relatedExecution',
            header: t('relatedExecution'),
            width: 180,
            minWidth: 140,
            Cell: SignalExecutionCell,
          },
      isRejectedView
        ? null
        : {
            colId: 'relatedFinding',
            header: t('relatedFinding'),
            width: 220,
            minWidth: 160,
            Cell: SignalFindingCell,
          },
      {
        colId: 'createdAt',
        header: t('created'),
        width: 150,
        minWidth: 130,
        Cell: SignalCreatedAtCell,
      },
    ];
    const dataColumns = columns
      .filter((column): column is SignalsDataColumn => column !== null)
      .map<ColDef<SignalsTableRow>>((column) => ({
        colId: column.colId,
        headerName: column.header,
        width: column.width,
        flex: column.flex,
        minWidth: column.minWidth,
        pinned: column.pinned,
        sortable: canSort && isSignalsSortableColumn(column.colId),
        comparator: preserveServerSortOrder,
        cellRenderer: renderSignalCell(column),
      }));

    if (isRejectedView) {
      return dataColumns;
    }

    return [
      {
        colId: 'select',
        headerName: '',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponent: SignalsTableSelectAllHeader,
        cellRenderer: renderSignalCell({ Cell: SignalsTableSelectionCell }),
      },
      ...dataColumns,
      {
        colId: 'actions',
        headerName: '',
        width: ACTIONS_COLUMN_WIDTH,
        minWidth: ACTIONS_COLUMN_WIDTH,
        maxWidth: ACTIONS_COLUMN_WIDTH,
        pinned: 'right',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: renderSignalCell({
          Cell: SignalActionsCell,
          justify: 'center',
        }),
      },
    ];
  }, [
    canSort,
    isRejectedView,
    showAppWideColumn,
    showSourceColumn,
    t,
    tFilters,
    tLifecycle,
  ]);
}
